import { useEffect, useState, type ReactNode } from 'react';
import { api, ErreurApi, requete, type Evenement, type PageJournal } from './api';
import { Filtres, type EtatFiltres } from './Filtres';
import { bornes, nombre } from './format';
import { IcClic, IcDesinscription, IcEchec, IcEnvoi, IcGarde, IcOuverture, IcRebond, IcRelance, IcReponse, IcTelecharger } from './icones';

const TYPES: [string, string][] = [
  ['envoi', 'Envois'], ['ouverture', 'Ouvertures'], ['clic', 'Clics'], ['reponse', 'Réponses'], ['rebond', 'Rebonds'], ['echec', 'Échecs'],
  ['desinscription', 'Désinscriptions'], ['relance', 'Relances'], ['relance_echec', 'Relances en échec'], ['garde_fou', 'Garde-fou'],
];
// Libellé d'une ligne (singulier), distinct du libellé des filtres (pluriel)
const LIBELLE: Record<string, string> = {
  envoi: 'Envoi', rebond: 'Rebond', echec: "Échec d'envoi", ouverture: 'Ouverture', clic: 'Clic', reponse: 'Réponse',
  desinscription: 'Désinscription', relance: 'Relance envoyée', relance_echec: 'Relance en échec', garde_fou: 'Garde-fou',
};
const ICONE: Record<string, ReactNode> = {
  envoi: <IcEnvoi />, rebond: <IcRebond />, echec: <IcEchec />, ouverture: <IcOuverture />, clic: <IcClic />, reponse: <IcReponse />,
  desinscription: <IcDesinscription />, relance: <IcRelance />, relance_echec: <IcEchec />, garde_fou: <IcGarde />,
};
const PAR_PAGE = 50;

const jourLong = (iso: string) => new Date(iso).toLocaleDateString('fr-FR', { timeZone: 'Europe/Paris', weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
const heure = (iso: string) => new Date(iso).toLocaleTimeString('fr-FR', { timeZone: 'Europe/Paris', hour: '2-digit', minute: '2-digit' });

function parJour(ev: Evenement[]): [string, Evenement[]][] {
  const groupes = new Map<string, Evenement[]>();
  for (const e of ev) {
    const j = jourLong(e.quand);
    groupes.set(j, [...(groupes.get(j) ?? []), e]);
  }
  return [...groupes.entries()];
}

export function Journal({ filtres, surFiltres }: { filtres: EtatFiltres; surFiltres: (f: EtatFiltres) => void }) {
  const [types, setTypes] = useState<string[]>([]);
  const [page, setPage] = useState(0);
  const [donnees, setDonnees] = useState<PageJournal | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const p = bornes(filtres.preset, filtres.du, filtres.au);
  const f = { campagne: filtres.campagne, du: p.du, au: p.au, types };
  const cle = JSON.stringify(f);

  useEffect(() => { setPage(0); }, [cle]);
  useEffect(() => {
    let vivant = true;
    setErreur(null);
    api.journal({ ...JSON.parse(cle), limite: PAR_PAGE, decalage: page * PAR_PAGE }).then((r) => { if (vivant) setDonnees(r); })
      .catch((e) => { if (vivant) setErreur(e instanceof ErreurApi && e.statut === 400 ? `Filtre refusé : ${e.message}` : 'Journal indisponible pour le moment.'); });
    return () => { vivant = false; };
  }, [cle, page]);

  const basculerType = (t: string) => setTypes((x) => (x.includes(t) ? x.filter((y) => y !== t) : [...x, t]));
  const pages = donnees ? Math.max(1, Math.ceil(donnees.total / PAR_PAGE)) : 1;

  return (
    <div className="journal">
      <header className="entete-page avec-actions">
        <div>
          <h1>Journal d'activité</h1>
          {donnees && <p className="aide" data-testid="journal-total">{nombre(donnees.total)} événement(s)</p>}
        </div>
        <a className="bouton secondaire" href={`/api/v2/journal/export.csv${requete(f)}`} download data-testid="export-journal"><IcTelecharger />Exporter (CSV)</a>
      </header>
      <Filtres valeur={filtres} surChangement={surFiltres} note="Ici, la période porte sur la date de chaque événement (heure de Paris)." />
      <div className="puces" role="group" aria-label="Types d'événements">
        {TYPES.map(([t, l]) => (
          <button key={t} className={`puce type-${t} ${types.includes(t) ? 'active' : ''}`} aria-pressed={types.includes(t)} onClick={() => basculerType(t)}>
            {ICONE[t]}{l}{donnees?.par_type[t] != null ? <span className="puce-compte">{nombre(donnees.par_type[t])}</span> : null}
          </button>
        ))}
      </div>
      {erreur && <p className="erreur" role="alert">{erreur}</p>}
      {donnees && (donnees.sans_date.envois > 0 || donnees.sans_date.echecs > 0 || donnees.sans_date.relances_echec > 0) && (
        <p className="note">
          Non datables (enregistrés sans date par l'ancien moteur, donc absents de la liste) :{' '}
          {[
            donnees.sans_date.envois > 0 && <span key="e"><span data-testid="sans-date-envois">{nombre(donnees.sans_date.envois)}</span> mail(s) parti(s)</span>,
            donnees.sans_date.echecs > 0 && <span key="f">{nombre(donnees.sans_date.echecs)} échec(s) d'envoi</span>,
            donnees.sans_date.relances_echec > 0 && <span key="r">{nombre(donnees.sans_date.relances_echec)} relance(s) en échec</span>,
          ].filter(Boolean).flatMap((x, i) => (i ? [', ', x] : [x]))}.
        </p>
      )}
      <p className="note">Une désinscription apparaît une fois, rattachée à la dernière campagne reçue ; l'indicateur « Désinscription » la compte dans chaque campagne envoyée avant elle.</p>
      {donnees && (
        <div className="fil" data-testid="journal-liste">
          {donnees.evenements.length === 0 && <p className="vide">Aucun événement pour ces filtres. Élargissez la période ou retirez un type.</p>}
          {parJour(donnees.evenements).map(([jour, ev]) => (
            <section key={jour} className="fil-jour" aria-label={jour}>
              <h2>{jour}</h2>
              <ol>
                {ev.map((e) => (
                  <li key={`${e.type}:${e.ref}`} className={`evenement type-${e.type}`}>
                    <time dateTime={e.quand}>{heure(e.quand)}</time>
                    <span className="pastille" aria-hidden="true">{ICONE[e.type]}</span>
                    <span className="evenement-corps">
                      <span className="evenement-tete"><b>{LIBELLE[e.type] ?? e.type}</b>{e.contact && <span>{e.contact}</span>}</span>
                      {(e.email || e.detail) && <span className="evenement-detail">{[e.email, e.detail].filter(Boolean).join(' — ')}</span>}
                    </span>
                    <span className="evenement-campagne">{e.campagne ?? (e.campagne_id != null ? `Campagne ${e.campagne_id}` : '')}</span>
                  </li>
                ))}
              </ol>
            </section>
          ))}
        </div>
      )}
      {donnees && pages > 1 && (
        <nav className="pagination" aria-label="Pages">
          <button className="secondaire" disabled={page === 0} onClick={() => setPage((x) => x - 1)}>Précédent</button>
          <span>Page {page + 1} / {pages}</span>
          <button className="secondaire" disabled={page + 1 >= pages} onClick={() => setPage((x) => x + 1)}>Suivant</button>
        </nav>
      )}
    </div>
  );
}
